/** SUNTRONIX ONE — Context Ribbon (responsive).
 *
 *  Desktop (≥ lg): full horizontal ribbon with pill destinations.
 *  Tablet / narrow  (< lg): compact "View ▾" trigger that opens a
 *                            bottom sheet listing every destination.
 *
 *  Solves the P0 §"iPad Portrait Section Selector" requirement — no
 *  ribbon overflow, no horizontal scrolling on narrow tablets.
 */
import { useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, LayoutGrid, Search } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const MAX_INLINE = 8;

export default function ContextRibbon({ spaceLabel, ribbon, actionSlot = null, leadingSlot = null }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Query-aware "best match" active item so vault-type tabs (…?type=BUSINESS)
  // and nested routes (…/inbox) each highlight correctly.
  const activeItem = useMemo(() => {
    if (!ribbon || ribbon.length === 0) return null;
    const matchFull = (to) => {
      const [p, qs] = to.split("?");
      if (location.pathname !== p && !location.pathname.startsWith(p + "/")) return false;
      if (qs) return location.search.includes(qs);
      return true;
    };
    const qMatch = ribbon.find((r) => r.to.includes("?") && matchFull(r.to));
    const pMatch = ribbon
      .filter((r) => !r.to.includes("?") && r.to !== "/" && matchFull(r.to))
      .sort((a, b) => b.to.length - a.to.length)[0];
    const rootMatch = ribbon.find((r) => r.to === "/" && location.pathname === "/");
    return qMatch || pMatch || rootMatch || ribbon[0];
  }, [ribbon, location.pathname, location.search]);
  const isActive = (r) => activeItem && r.to === activeItem.to;

  if (!ribbon || ribbon.length === 0) return null;

  const hasOverflow = ribbon.length > MAX_INLINE;
  let inline = ribbon;
  if (hasOverflow) {
    inline = ribbon.slice(0, MAX_INLINE);
    if (activeItem && !inline.some((r) => r.to === activeItem.to)) {
      inline = [...inline.slice(0, MAX_INLINE - 1), activeItem];
    }
  }

  return (
    <nav
      aria-label="Context ribbon"
      data-testid="context-ribbon"
      className="border-b border-[color:var(--st-border)] bg-[color:var(--st-bg)] px-4 md:px-6 shrink-0"
    >
      {/* ---------- Desktop ribbon (≥ lg) ---------- */}
      <div className="hidden lg:flex h-11 items-center gap-1">
        {leadingSlot ? (
          <div className="flex items-center shrink-0 mr-1 pr-2 border-r border-[color:var(--st-border)]" data-testid="ribbon-leading">
            {leadingSlot}
          </div>
        ) : (
          <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)] shrink-0 mr-3">
            {spaceLabel}
          </div>
        )}
        <div className="flex items-center gap-1 min-w-0 flex-1">
          {inline.map((r) => (
            <Link
              key={r.to}
              to={r.to}
              data-testid={`ribbon-${r.to.replace(/[/?=]/g, "-")}`}
              className={[
                "h-8 px-3 rounded-md text-[13px] font-medium transition-colors shrink-0 border-b-2 flex items-center",
                isActive(r)
                  ? "text-[color:var(--st-text)] border-[color:var(--st-accent-space,var(--st-accent))]"
                  : "text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] border-transparent",
              ].join(" ")}
            >
              {r.label}
            </Link>
          ))}
          {hasOverflow && (
            <AllViewsMenu spaceLabel={spaceLabel} ribbon={ribbon} activeItem={activeItem} />
          )}
        </div>
        {actionSlot && (
          <div className="shrink-0 pl-3 ml-2 border-l border-[color:var(--st-border)]" data-testid="context-ribbon-actions">
            {actionSlot}
          </div>
        )}
      </div>

      {/* ---------- Compact "View ▾" (< lg) ---------- */}
      <div className="flex lg:hidden h-12 items-center justify-between gap-2">
        {leadingSlot && (
          <div className="shrink-0" data-testid="ribbon-leading-compact">{leadingSlot}</div>
        )}
        <div className="min-w-0 flex-1">
          {!leadingSlot && (
            <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-muted)] leading-none">
              {spaceLabel}
            </div>
          )}
          <div className="text-[14px] font-medium text-[color:var(--st-text)] truncate leading-tight mt-0.5">
            {activeItem?.label}
          </div>
        </div>

        {actionSlot && (
          <div className="shrink-0" data-testid="context-ribbon-actions-compact">
            {actionSlot}
          </div>
        )}

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="context-ribbon-view-trigger"
              className="h-9 px-3 rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface)] text-[13px] font-medium text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)] flex items-center gap-1.5 min-h-[40px]"
              aria-label={`Change view (currently ${activeItem?.label})`}
            >
              <LayoutGrid className="h-4 w-4 text-[color:var(--st-accent)]" strokeWidth={1.6} />
              <span>View</span>
              <ChevronDown className="h-3.5 w-3.5 text-[color:var(--st-text-muted)]" />
            </button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            data-testid="context-ribbon-view-sheet"
            className="rounded-t-2xl border-t border-[color:var(--st-border)] p-0 max-h-[80dvh]"
          >
            <SheetHeader className="px-5 pt-4 pb-2 text-left">
              <div className="mx-auto h-1 w-10 rounded-full bg-slate-300 mb-3" aria-hidden="true" />
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">
                {spaceLabel}
              </div>
              <SheetTitle className="text-[16px] font-heading font-semibold text-[color:var(--st-text)]">
                Choose a view
              </SheetTitle>
            </SheetHeader>
            <div className="px-3 pb-6 pt-2 grid grid-cols-1 gap-1 overflow-y-auto">
              {ribbon.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  onClick={() => setOpen(false)}
                  data-testid={`ribbon-sheet-${r.to.replace(/[/?=]/g, "-")}`}
                  className={[
                    "w-full flex items-center justify-between px-3 py-3 rounded-lg text-[14px] min-h-[48px] transition-colors",
                    isActive(r)
                      ? "bg-[color:var(--st-text)] text-white"
                      : "text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)]",
                  ].join(" ")}
                >
                  <span>{r.label}</span>
                  {isActive(r) && (
                    <span className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--st-accent)]">
                      Current
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
}

/** Rich "All views" launcher — searchable grid of every destination in the
 *  space. Removes horizontal scrolling in large spaces (Operations,
 *  Insights, Administration, …). */
function AllViewsMenu({ spaceLabel, ribbon, activeItem }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return ribbon;
    const s = q.trim().toLowerCase();
    return ribbon.filter((r) => r.label.toLowerCase().includes(s));
  }, [q, ribbon]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="ribbon-all-views"
          aria-expanded={open}
          className="h-8 px-3 ml-1 rounded-full text-[12.5px] font-semibold flex items-center gap-1.5 shrink-0 border border-[color:var(--st-border)] bg-[color:var(--st-surface)] text-[color:var(--st-text)] hover:border-[color:var(--st-accent)] hover:text-[color:var(--st-accent)] transition-colors"
        >
          <LayoutGrid className="h-3.5 w-3.5 text-[color:var(--st-accent)]" strokeWidth={1.8} />
          All views
          <span className="text-[9.5px] font-mono px-1.5 py-0.5 rounded-full bg-[color:var(--st-surface-alt)] text-[color:var(--st-text-muted)]">
            {ribbon.length}
          </span>
          <ChevronDown className="h-3 w-3 text-[color:var(--st-text-muted)]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        data-testid="ribbon-all-views-panel"
        className="w-[640px] max-w-[92vw] p-0 rounded-xl border border-[color:var(--st-border)] shadow-xl overflow-hidden"
      >
        <div className="px-4 pt-3.5 pb-2.5 border-b border-[color:var(--st-border)] bg-[color:var(--st-surface-alt)]">
          <div className="text-[9.5px] uppercase tracking-[0.2em] text-[color:var(--st-text-muted)]">
            {spaceLabel}
          </div>
          <div className="flex items-center justify-between gap-3 mt-0.5">
            <div className="text-[15px] font-heading font-semibold text-[color:var(--st-text)]">
              All views
            </div>
            <div className="relative w-56">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--st-text-muted)]" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Type to filter…"
                data-testid="ribbon-all-views-search"
                className="w-full h-8 pl-8 pr-2 text-[12px] rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface)] outline-none focus:border-[color:var(--st-accent)]"
              />
            </div>
          </div>
        </div>
        <div className="p-3 grid grid-cols-3 gap-1.5 max-h-[420px] overflow-y-auto">
          {filtered.map((r) => (
            <NavLink
              key={r.to}
              to={r.to}
              end={r.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`ribbon-grid-${r.to.replace(/\//g, "-")}`}
              className={
                "group flex items-center gap-2 px-3 py-2.5 rounded-lg text-[12.5px] font-medium transition-colors border " +
                (activeItem?.to === r.to
                  ? "bg-[color:var(--st-text)] text-white border-transparent"
                  : "border-transparent text-[color:var(--st-text)] hover:bg-[color:var(--st-surface-alt)] hover:border-[color:var(--st-border)]")
              }
            >
              <span className={
                "h-1.5 w-1.5 rounded-full shrink-0 " +
                (activeItem?.to === r.to
                  ? "bg-[color:var(--st-accent)]"
                  : "bg-[color:var(--st-border)] group-hover:bg-[color:var(--st-accent)]")
              } />
              <span className="truncate">{r.label}</span>
            </NavLink>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-3 text-center text-[12px] text-[color:var(--st-text-muted)] py-6">
              No view matches “{q}”.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
